import { useState, useCallback, useMemo } from 'react'
import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { useAppView } from '@/hooks/useAppView'
import { getAdapter } from '@/adapters/registry'
import { getAllAdaptersWithSessions } from '@/adapters/multiAdapter'
import type { SearchResult } from '@/adapters/types'
import type { Post, PostsPage } from '@/types/posts'
import type { Platform } from '@/contexts/ProtocolContext'
import { SEARCH_MIN_CHARS } from '@/constants/search'

// Re-export for backwards compatibility
export type { SearchResult } from '@/adapters/types'

export interface HashtagResult {
    name: string
    url?: string
    history?: Array<{ uses: string }>
}

interface MastodonHashtag {
    name: string
    url: string
    history: Array<{ uses: string }>
}

/**
 * Hook for searching users across Bluesky and/or Mastodon/Pixelfed.
 * Accepts a platform filter of 'bluesky', 'mastodon', 'pixelfed', or 'all'.
 * When 'all', all protocols are queried in parallel via Promise.allSettled
 * and their results are interleaved (alternating bluesky/fediverse).
 * Uses React Query for caching and request management.
 */
export function useUserSearch(query: string, platform: Platform | 'all') {
    const { session, mastodonSession, pixelfedSession } = useAuth()
    const { appView, publicAgent } = useAppView()

    const hasAnySession = !!session || !!mastodonSession || !!pixelfedSession
    const hasSession =
        platform === 'all'
            ? hasAnySession
            : platform === 'bluesky'
              ? !!session
              : platform === 'pixelfed'
                ? !!pixelfedSession
                : !!mastodonSession
    const isEnabled = query.length >= SEARCH_MIN_CHARS && hasSession

    // Stable identifiers for cache keying — invalidate when logged-in user changes
    const blueskyDid = session?.did ?? null
    const mastodonAcct = mastodonSession?.account?.acct ?? null
    const pixelfedAcct = pixelfedSession?.account?.acct ?? null

    return useQuery<SearchResult[], Error>({
        queryKey: [
            'search',
            'users',
            platform,
            query,
            appView,
            blueskyDid,
            mastodonAcct,
            pixelfedAcct,
        ],
        queryFn: async () => {
            if (platform !== 'all') {
                // Single-platform path via adapter
                const adapterSession =
                    platform === 'bluesky'
                        ? session
                        : platform === 'mastodon'
                          ? mastodonSession
                          : pixelfedSession
                if (!adapterSession) return []
                return getAdapter(platform).searchUsers(adapterSession, query, { publicAgent })
            }

            // 'all' mode: parallel requests to all active adapters
            const active = getAllAdaptersWithSessions({ session, mastodonSession, pixelfedSession })
            const tasks = active.map(({ adapter, session: s }) =>
                adapter.searchUsers(s, query, { publicAgent })
            )
            const settled = await Promise.allSettled(tasks)

            // Collect fulfilled results per platform, preserving order
            const blueskyResults: SearchResult[] = []
            const fediverseResults: SearchResult[] = []

            for (const result of settled) {
                if (result.status === 'fulfilled') {
                    for (const item of result.value) {
                        if (item.platform === 'bluesky') blueskyResults.push(item)
                        else fediverseResults.push(item)
                    }
                }
            }

            // Interleave: bluesky[0], fediverse[0], bluesky[1], fediverse[1], ...
            const interleaved: SearchResult[] = []
            const maxLen = Math.max(blueskyResults.length, fediverseResults.length)
            for (let i = 0; i < maxLen; i++) {
                if (i < blueskyResults.length) interleaved.push(blueskyResults[i])
                if (i < fediverseResults.length) interleaved.push(fediverseResults[i])
            }

            return interleaved
        },
        enabled: isEnabled,
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes
        // A failed AppView search (e.g. Blacksky's 502) should surface immediately
        // instead of retrying 3x and leaving skeletons up for several seconds.
        retry: false,
    })
}

/** Dual cursors for cross-platform post search pagination */
interface SearchCursors {
    bluesky?: string | null
    mastodon?: string | null
    pixelfed?: string | null
}

/**
 * Hook for searching posts across Bluesky and/or Mastodon/Pixelfed with infinite scroll.
 * Uses useInfiniteQuery with cursor-based pagination (Bluesky) and offset-based
 * pagination (Mastodon/Pixelfed). In cross-platform mode, tracks dual cursors and skips
 * exhausted protocols.
 * Only fires when `enabled` is true (on-demand, controlled by active tab).
 */
export function usePostSearch(query: string, platform: Platform | 'all', enabled: boolean) {
    const { session, mastodonSession, pixelfedSession } = useAuth()
    const { appView, publicAgent } = useAppView()

    const hasAnySession = !!session || !!mastodonSession || !!pixelfedSession
    const hasSession =
        platform === 'all'
            ? hasAnySession
            : platform === 'bluesky'
              ? !!session
              : platform === 'pixelfed'
                ? !!pixelfedSession
                : !!mastodonSession
    const isEnabled = enabled && query.length >= SEARCH_MIN_CHARS && hasSession

    const blueskyDid = session?.did ?? null
    const mastodonAcct = mastodonSession?.account?.acct ?? null
    const pixelfedAcct = pixelfedSession?.account?.acct ?? null
    const queryKey = [
        'search',
        'posts',
        platform,
        query,
        appView,
        blueskyDid,
        mastodonAcct,
        pixelfedAcct,
    ] as const

    const infiniteQuery = useInfiniteQuery<
        PostsPage,
        Error,
        { pages: PostsPage[] },
        typeof queryKey,
        SearchCursors | undefined
    >({
        queryKey,
        queryFn: async ({ pageParam }) => {
            if (platform !== 'all') {
                // Single-platform path via adapter
                const adapterSession =
                    platform === 'bluesky'
                        ? session
                        : platform === 'mastodon'
                          ? mastodonSession
                          : pixelfedSession
                if (!adapterSession) return { posts: [] }
                const cursor =
                    platform === 'bluesky'
                        ? (pageParam?.bluesky ?? undefined)
                        : platform === 'pixelfed'
                          ? (pageParam?.pixelfed ?? undefined)
                          : (pageParam?.mastodon ?? undefined)
                return getAdapter(platform).searchPosts(adapterSession, query, cursor, {
                    publicAgent,
                })
            }

            // 'all' mode: parallel requests to all active adapters
            const blueskyExhausted = pageParam?.bluesky === null
            const mastodonExhausted = pageParam?.mastodon === null
            const pixelfedExhausted = pageParam?.pixelfed === null

            const promises: Promise<PostsPage>[] = []
            const order: Array<'bluesky' | 'mastodon' | 'pixelfed'> = []

            if (!blueskyExhausted && session) {
                promises.push(
                    getAdapter('bluesky').searchPosts(
                        session,
                        query,
                        pageParam?.bluesky ?? undefined,
                        { publicAgent }
                    )
                )
                order.push('bluesky')
            }
            if (!mastodonExhausted && mastodonSession) {
                promises.push(
                    getAdapter('mastodon').searchPosts(
                        mastodonSession,
                        query,
                        pageParam?.mastodon ?? undefined
                    )
                )
                order.push('mastodon')
            }
            if (!pixelfedExhausted && pixelfedSession) {
                promises.push(
                    getAdapter('pixelfed').searchPosts(
                        pixelfedSession,
                        query,
                        pageParam?.pixelfed ?? undefined
                    )
                )
                order.push('pixelfed')
            }

            const settled = await Promise.allSettled(promises)

            const blueskyPosts: Post[] = []
            const fediversePosts: Post[] = []
            let bluesky: string | undefined
            let mastodon: string | undefined
            let pixelfed: string | undefined

            settled.forEach((result, i) => {
                if (result.status !== 'fulfilled') return
                const proto = order[i]
                for (const post of result.value.posts) {
                    if (proto === 'bluesky') blueskyPosts.push(post)
                    else fediversePosts.push(post)
                }
                if (proto === 'bluesky') bluesky = result.value.cursor
                else if (proto === 'mastodon') mastodon = result.value.mastodonCursor
                else pixelfed = result.value.pixelfedCursor
            })

            // Interleave: bluesky[0], fediverse[0], bluesky[1], fediverse[1], ...
            const interleaved: Post[] = []
            const maxLen = Math.max(blueskyPosts.length, fediversePosts.length)
            for (let i = 0; i < maxLen; i++) {
                if (i < blueskyPosts.length) interleaved.push(blueskyPosts[i])
                if (i < fediversePosts.length) interleaved.push(fediversePosts[i])
            }

            return {
                posts: interleaved,
                cursor: order.includes('bluesky') ? bluesky : undefined,
                mastodonCursor: order.includes('mastodon') ? mastodon : undefined,
                pixelfedCursor: order.includes('pixelfed') ? pixelfed : undefined,
            }
        },
        initialPageParam: undefined as SearchCursors | undefined,
        getNextPageParam: (lastPage): SearchCursors | undefined => {
            if (platform === 'all') {
                const bluesky = lastPage.cursor ?? null
                const mastodon = lastPage.mastodonCursor ?? null
                const pixelfed = lastPage.pixelfedCursor ?? null
                if (bluesky === null && mastodon === null && pixelfed === null) return undefined
                return { bluesky, mastodon, pixelfed }
            }
            if (platform === 'bluesky') {
                return lastPage.cursor ? { bluesky: lastPage.cursor } : undefined
            }
            if (platform === 'mastodon') {
                return lastPage.mastodonCursor ? { mastodon: lastPage.mastodonCursor } : undefined
            }
            // pixelfed
            return lastPage.pixelfedCursor ? { pixelfed: lastPage.pixelfedCursor } : undefined
        },
        enabled: isEnabled,
        staleTime: 2 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
        // A failed AppView search (e.g. Blacksky's 502) should surface immediately
        // instead of retrying 3x and leaving skeletons up for several seconds.
        retry: false,
    })

    const posts = useMemo(
        () => infiniteQuery.data?.pages.flatMap(page => page.posts) ?? [],
        [infiniteQuery.data]
    )

    return {
        posts,
        isLoading: infiniteQuery.isLoading,
        isFetchingNextPage: infiniteQuery.isFetchingNextPage,
        hasNextPage: infiniteQuery.hasNextPage ?? false,
        fetchNextPage: infiniteQuery.fetchNextPage,
        error: infiniteQuery.error,
        refetch: infiniteQuery.refetch,
        isRefetching: infiniteQuery.isRefetching,
    }
}

/**
 * Legacy callback-based search hook for autocomplete functionality.
 * Used by RichComposer for @mentions and #hashtags.
 */
export function useSearch() {
    const { session, mastodonSession, pixelfedSession } = useAuth()
    const { publicAgent } = useAppView()
    const [isSearching, setIsSearching] = useState(false)

    const searchUsers = useCallback(
        async (query: string, platform?: Platform): Promise<SearchResult[]> => {
            if (!query || query.length < 2) return []

            setIsSearching(true)
            try {
                if (platform) {
                    const adapterSession =
                        platform === 'bluesky'
                            ? session
                            : platform === 'mastodon'
                              ? mastodonSession
                              : pixelfedSession
                    if (!adapterSession) return []
                    return await getAdapter(platform).searchUsers(adapterSession, query, {
                        publicAgent,
                    })
                }

                // No platform specified — search all available sessions
                const active = getAllAdaptersWithSessions({
                    session,
                    mastodonSession,
                    pixelfedSession,
                })
                const tasks = active.map(({ adapter, session: s }) =>
                    adapter.searchUsers(s, query, { publicAgent })
                )
                const settled = await Promise.allSettled(tasks)
                const results: SearchResult[] = []
                for (const result of settled) {
                    if (result.status === 'fulfilled') results.push(...result.value)
                }
                return results
            } catch (err) {
                console.error('Search failed:', err)
                return []
            } finally {
                setIsSearching(false)
            }
        },
        [session, mastodonSession, pixelfedSession, publicAgent]
    )

    const searchHashtags = useCallback(
        async (query: string): Promise<HashtagResult[]> => {
            if (!query || query.length < 1) return []
            // Use Mastodon or Pixelfed session for hashtag search (both support the same API)
            const fediverseSession = mastodonSession ?? pixelfedSession
            if (!fediverseSession) return []

            setIsSearching(true)
            const results: HashtagResult[] = []

            try {
                const res = await fetch(
                    `${fediverseSession.instance_url}/api/v2/search?q=${encodeURIComponent(query)}&type=hashtags&limit=5`,
                    {
                        headers: {
                            Authorization: `Bearer ${fediverseSession.access_token}`,
                        },
                    }
                )

                if (res.ok) {
                    const data = await res.json()
                    results.push(
                        ...data.hashtags.map((hashtag: MastodonHashtag) => ({
                            name: hashtag.name,
                            url: hashtag.url,
                            history: hashtag.history,
                        }))
                    )
                }
            } catch (err) {
                console.error('Hashtag search failed:', err)
            } finally {
                setIsSearching(false)
            }

            return results
        },
        [mastodonSession, pixelfedSession]
    )

    return {
        searchUsers,
        searchHashtags,
        isSearching,
    }
}
